import { useEffect, useState } from 'react';
import { CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';
import AdminLayout from '@/components/AdminLayout';

const ALL_TIMES = Array.from({ length: 22 }, (_, i) => `${String(9 + Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
const fmtT = (t) => { const [h, m] = t.split(':').map(Number); return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; };

export default function AdminCalendar() {
  const [bookings, setBookings] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [blockDate, setBlockDate] = useState('');
  const [hourDate, setHourDate] = useState('');
  const [hourBlocked, setHourBlocked] = useState([]);

  const load = () => {
    api.get('/bookings').then((r) => setBookings(r.data)).catch(() => setBookings([]));
    api.get('/blocked-dates').then((r) => setBlocked(r.data.dates)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!hourDate) return;
    api.get(`/blocked-slots?date=${hourDate}`).then((r) => setHourBlocked(r.data.times)).catch(() => setHourBlocked([]));
  }, [hourDate]);

  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch { toast.error('Could not update booking.'); }
  };

  const addBlocked = async () => {
    if (!blockDate) return;
    try {
      await api.post('/blocked-dates', { date: blockDate });
      setBlocked((b) => [...new Set([...b, blockDate])].sort());
      setBlockDate('');
      toast.success('Date blocked — no bookings can be made that day.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not block that date.'); }
  };

  const removeBlocked = async (d) => {
    try {
      await api.delete(`/blocked-dates/${d}`);
      setBlocked((b) => b.filter((x) => x !== d));
    } catch { toast.error('Could not unblock that date.'); }
  };

  const toggleHour = async (t) => {
    try {
      const { data } = await api.post('/blocked-slots', { date: hourDate, time: t });
      setHourBlocked((b) => (data.blocked ? [...b, t].sort() : b.filter((x) => x !== t)));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update that hour.'); }
  };

  return (
    <AdminLayout title="Bookings & Calendar" subtitle="Consultation schedule — 9:00 AM to 8:00 PM ET, weekdays. Block days or individual hours." testId="admin-calendar-page">
      <div className="mb-10">
        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <CalendarDays size={14} /> Consultation Bookings
        </h2>
        {bookings === null ? (
          <div className="flex justify-center py-10"><Loader2 size={20} className="text-amber-400 animate-spin" /></div>
        ) : bookings.length === 0 ? (
          <p className="text-sm text-slate-500 border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">No bookings yet — share your /book link.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} data-testid="owner-booking-row" className="flex flex-wrap items-center justify-between gap-3 border border-slate-800 rounded-xl bg-[#0F1C33]/60 px-5 py-4">
                <div>
                  <div className="text-sm text-slate-200">{b.name} <span className="text-slate-500">· {b.email}</span></div>
                  <div className="font-mono-tech text-[11px] text-amber-300/80 mt-1">{b.date} · {b.time} ET</div>
                </div>
                <select data-testid="booking-status-select" value={b.status} onChange={(e) => setBookingStatus(b.id, e.target.value)}
                  className="bg-[#0B1528] border border-slate-700 rounded-lg text-xs px-3 py-2 text-slate-200 outline-none">
                  <option value="confirmed">Confirmed</option>
                  <option value="done">Done</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-mono-tech text-xs tracking-[0.3em] text-amber-400/90 uppercase mb-5 flex items-center gap-2">
          <CalendarDays size={14} /> Unavailable Dates & Hours
        </h2>
        <div className="border border-slate-800 rounded-2xl bg-[#0F1C33]/60 p-6">
          <div className="flex gap-3 mb-4">
            <input data-testid="block-date-input" type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)}
              className="bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors [color-scheme:dark]" />
            <button data-testid="block-date-button" onClick={addBlocked} disabled={!blockDate}
              className="text-sm border border-amber-500/40 text-amber-300 rounded-full px-6 hover:bg-amber-500/10 transition-colors disabled:opacity-40">
              Block this day
            </button>
          </div>
          {blocked.length === 0 ? (
            <p className="text-xs text-slate-500">No blocked dates — every weekday is bookable.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {blocked.map((d) => (
                <span key={d} data-testid="blocked-date-chip" className="inline-flex items-center gap-2 font-mono-tech text-xs border border-red-500/30 bg-red-500/5 text-red-300 rounded-full px-4 py-1.5">
                  {d}
                  <button data-testid="blocked-date-remove" onClick={() => removeBlocked(d)} className="hover:text-red-100">✕</button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-5 pt-5 border-t border-slate-800">
            <p className="text-xs text-slate-500 mb-3">Or block specific hours on a day (9:00 AM – 8:00 PM ET):</p>
            <input data-testid="block-hours-date-input" type="date" value={hourDate} onChange={(e) => setHourDate(e.target.value)}
              className="bg-[#0B1528]/70 border border-slate-700/60 focus:border-amber-500/60 rounded-xl px-4 py-2.5 text-sm outline-none transition-colors [color-scheme:dark] mb-3" />
            {hourDate && (
              <div data-testid="block-hours-grid" className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {ALL_TIMES.map((t) => {
                  const isBlocked = hourBlocked.includes(t);
                  return (
                    <button key={t} data-testid="block-hour-toggle" onClick={() => toggleHour(t)}
                      className={`rounded-lg border py-2 font-mono-tech text-[11px] transition-colors duration-200 ${
                        isBlocked ? 'border-red-500/50 bg-red-500/10 text-red-300 line-through' : 'border-slate-700/60 text-slate-300 hover:border-amber-500/40'}`}>
                      {fmtT(t)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
